import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services/auth';
import { logger } from '../utils/logger';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  token: string | null;
  /** Rend l'utilisateur connecté (#262 : la page de connexion choisit où revenir). */
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  /**
   * #194 (D3) — le profil n'a pas pu être chargé pour une raison qui n'est PAS
   * un refus d'authentification (429, réseau, 5xx) et les nouvelles tentatives
   * sont épuisées. La session est CONSERVÉE (jetons intacts) : l'écran propose
   * de réessayer (`retryProfile`) au lieu de déconnecter.
   */
  profileError: boolean;
  retryProfile: () => void;
}

/** Recul entre deux tentatives de chargement du profil (#194, D3). */
export const PROFILE_RETRY_DELAYS_MS = [1000, 2000, 4000] as const;

/** Seul un refus d'authentification justifie de jeter la session. */
export function isAuthRejection(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 || status === 403;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [profileError, setProfileError] = useState(false);
  const [profileAttempt, setProfileAttempt] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    
    const initAuth = async () => {
      // Check if user has a token first
      const token = authService.getAccessToken();
      if (!token) {
        // No token, user is not authenticated
        if (isMounted) {
          setUser(null);
          setIsLoading(false);
        }
        return;
      }
    
      // Token exists, try to get profile.
      // #194 (D3) : l'ancien `.catch` déconnectait sur TOUTE erreur et vidait le
      // localStorage — un 429, une coupure réseau ou un 5xx au chargement
      // éjectait l'agent. Seul un 401/403 (jeton refusé, après la tentative de
      // refresh d'api.ts) jette la session ; le reste est réessayé avec recul,
      // puis rendu comme un état d'erreur réessayable, jetons intacts.
      logger.debug('🔄 AuthContext - Fetching user profile...')
      for (let attempt = 0; ; attempt++) {
        try {
          const u = await authService.getProfile();
          logger.debug('✅ AuthContext - Profile loaded:', u.email)
          if (isMounted) {
            setUser(u);
            setToken(token);
            setProfileError(false);
          }
          break;
        } catch (error) {
          logger.error('❌ AuthContext - Failed to load profile:', error)
          if (isAuthRejection(error)) {
            authService.logout();
            if (isMounted) {
              setUser(null);
              setToken(null);
            }
            break;
          }
          if (attempt >= PROFILE_RETRY_DELAYS_MS.length) {
            if (isMounted) setProfileError(true);
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, PROFILE_RETRY_DELAYS_MS[attempt]));
          if (!isMounted) return;
        }
      }
      if (isMounted) setIsLoading(false);
    };
    
    initAuth();
      
    return () => {
      isMounted = false;
    };
  }, [profileAttempt]);

  const retryProfile = () => {
    setProfileError(false);
    setProfileAttempt((n) => n + 1);
  };

  // ✅ HEARTBEAT: Presence tracking (every 30s) + force-logout detection
  useEffect(() => {
    if (!user) return;

    const HEARTBEAT_INTERVAL = 30 * 1000; // 30 seconds

    const sendHeartbeat = async () => {
      try {
        const result = await authService.heartbeat();
        if (result.forceLogout) {
          logger.warn('🚨 Force logout detected by admin!');
          authService.logout();
          setUser(null);
          setToken(null);
          window.location.href = '/login?reason=force_logout';
          return;
        }
      } catch (error) {
        // Heartbeat failure is non-critical, don't logout
        logger.debug('⚠️ Heartbeat failed (non-critical):', error);
      }
    };

    // Send first heartbeat immediately on login
    sendHeartbeat();
    const heartbeatId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    return () => clearInterval(heartbeatId);
  }, [user]);

  // ✅ KEEP-ALIVE: Refresh token périodique pour éviter l'expiration de session
  // Refresh toutes les 45 minutes (avant l'expiration du token de 1h)
  useEffect(() => {
    if (!user) return; // Pas connecté, pas de refresh

    const REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes en ms
    
    const refreshToken = async () => {
      try {
        const refreshTokenValue = localStorage.getItem('refreshToken');
        if (!refreshTokenValue) {
          logger.warn('⚠️ No refresh token available for keep-alive');
          return;
        }
        
        // Appeler l'endpoint de refresh silencieusement
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refreshTokenValue })
        });
        
        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('accessToken', data.accessToken);
          // ✅ FIX BUG B: CRITICAL — Also save new refresh token (rotation)
          // Without this, the old token is revoked but still in localStorage → next refresh = 401
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
          }
          setToken(data.accessToken);
          logger.debug('🔄 Session keep-alive: token refreshed silently');
        } else {
          logger.warn('⚠️ Session keep-alive failed, will retry on next interval');
        }
      } catch (error) {
        logger.error('❌ Session keep-alive error:', error);
      }
    };

    // Premier refresh après 45 minutes, puis toutes les 45 minutes
    const intervalId = setInterval(refreshToken, REFRESH_INTERVAL);
    
    // ⚠️ NOTE: visibilitychange handler moved to api.ts (centralized wake-up detection)
    // The old handler here used raw fetch() which raced with api.ts's axios-based refresh,
    // causing token rotation conflicts and 401 storms on iPad wake-up.
    // See api.ts WAKE-UP DETECTION section for the coordinated implementation.

    return () => {
      clearInterval(intervalId);
    };
  }, [user]);

  const login = async (email: string, password: string) => {
    try {
      logger.info('🔐 AuthContext - Starting login process...');
      const response = await authService.login({ email, password });
      
      authService.setTokens(response.accessToken, response.refreshToken);
      setUser(response.user);
      setToken(response.accessToken);
      setProfileError(false);
      
      logger.info('🎉 AuthContext - Login successful:', response.user.email);

      
      // ✅ PERF: Prefetch critical data after login for instant navigation
      // These calls run in background, don't block the UI
      setTimeout(() => {
        logger.debug('🚀 Prefetching critical data...');
        // Prefetch is handled by React Query's staleTime - 
        // first visit to pages will cache data automatically
      }, 100);
      return response.user;
    } catch (error) {
      logger.error('❌ AuthContext - Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    logger.info('🚪 AuthContext - Logging out...');
    authService.logout();
    setUser(null);
    setToken(null);
    setProfileError(false);
    logger.info('✅ AuthContext - Logout complete');
  };

  const refreshProfile = async () => {
    try {
      const currentToken = authService.getAccessToken();
      if (!currentToken) {
        logger.warn('⚠️ AuthContext - Cannot refresh profile: no token');
        return;
      }

      logger.debug('🔄 AuthContext - Refreshing profile...');
      const refreshedUser = await authService.getProfile();
      setUser(refreshedUser);
      logger.debug('✅ AuthContext - Profile refreshed:', refreshedUser.email);
    } catch (error) {
      logger.error('❌ AuthContext - Failed to refresh profile:', error);
      // Don't logout on refresh failure, just log the error
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, token, login, logout, refreshProfile, profileError, retryProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
